import { useSOCAnalytics, useSOCBenchmarks, type SOCAnalyticsData, type BenchmarkComparison } from '../../hooks/useSecOps';

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#d97706',
  low: '#2563eb',
  info: '#6b7280',
};

function MetricCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function BarChart({ data, maxH = 120 }: { data: Array<{ label: string; value: number; color?: string }>; maxH?: number }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-1" style={{ height: maxH }}>
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
          <div
            className="w-full rounded-t transition-all"
            style={{ height: `${(d.value / max) * 100}%`, minHeight: d.value > 0 ? 2 : 0, backgroundColor: d.color ?? '#6366f1' }}
            title={`${d.label}: ${d.value}`}
          />
          <span className="text-[9px] text-gray-400 mt-1 truncate max-w-full">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function SeverityDonut({ breakdown }: { breakdown: SOCAnalyticsData['severity_breakdown'] }) {
  const total = Object.values(breakdown).reduce((a, b) => a + b, 0);
  if (total === 0) return <p className="text-sm text-gray-400 text-center py-8">No alerts</p>;

  const entries = Object.entries(breakdown).filter(([, v]) => v > 0);
  let cumulative = 0;

  return (
    <div className="flex items-center gap-6">
      <div className="relative w-28 h-28 flex-shrink-0">
        <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
          {entries.map(([sev, count]) => {
            const pct = (count / total) * 100;
            const offset = cumulative;
            cumulative += pct;
            return (
              <circle
                key={sev}
                cx="18" cy="18" r="15.9155"
                fill="none"
                stroke={SEVERITY_COLORS[sev] ?? '#9ca3af'}
                strokeWidth="3"
                strokeDasharray={`${pct} ${100 - pct}`}
                strokeDashoffset={`${-offset}`}
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-gray-900">{total}</span>
          <span className="text-[9px] text-gray-400">alerts</span>
        </div>
      </div>
      <div className="space-y-1">
        {entries.map(([sev, count]) => (
          <div key={sev} className="flex items-center gap-2 text-xs">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: SEVERITY_COLORS[sev] }} />
            <span className="capitalize text-gray-600">{sev}</span>
            <span className="font-semibold text-gray-900 ml-auto">{count}</span>
            <span className="text-gray-400 w-8 text-right">{Math.round((count / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BenchmarkCard({ label, comparison, unit = 'm', lowerIsBetter = true }: {
  label: string;
  comparison: BenchmarkComparison;
  unit?: string;
  lowerIsBetter?: boolean;
}) {
  const yours = comparison.yours;
  const delta = comparison.delta_pct;
  const isBetter = delta != null && (lowerIsBetter ? delta > 0 : delta > 0);

  return (
    <div className="bg-white rounded-lg shadow p-4 border-l-4" style={{ borderLeftColor: comparison.ranking ? (comparison.ranking.percentile >= 75 ? '#16a34a' : comparison.ranking.percentile >= 50 ? '#2563eb' : comparison.ranking.percentile >= 25 ? '#d97706' : '#dc2626') : '#9ca3af' }}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-2xl font-bold text-gray-900">{yours != null ? `${yours}${unit}` : '--'}</span>
        {delta != null && (
          <span className={`text-sm font-semibold ${isBetter ? 'text-green-600' : 'text-red-600'}`}>
            {isBetter ? '+' : ''}{delta}% vs avg
          </span>
        )}
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[10px] text-gray-400">Community avg: {comparison.community_avg}{unit}</span>
        {comparison.ranking && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
            comparison.ranking.percentile >= 75 ? 'bg-green-100 text-green-700' :
            comparison.ranking.percentile >= 50 ? 'bg-blue-100 text-blue-700' :
            comparison.ranking.percentile >= 25 ? 'bg-amber-100 text-amber-700' :
            'bg-red-100 text-red-700'
          }`}>
            {comparison.ranking.label}
          </span>
        )}
      </div>
      {comparison.community_p50 != null && (
        <span className="text-[10px] text-gray-400">Median: {comparison.community_p50}{unit}</span>
      )}
    </div>
  );
}

export default function SecurityAnalyticsPage() {
  const { data: response, loading, error } = useSOCAnalytics();
  const { data: benchmarkResponse } = useSOCBenchmarks();
  const analytics = response?.data;
  const benchmarks = benchmarkResponse?.data;

  if (loading && !analytics) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Security Analytics</h1>
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="bg-gray-200 h-20 rounded-lg" />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="bg-gray-200 h-56 rounded-lg" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Security Analytics</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  if (!analytics) return null;

  const { summary, alert_volume_trend, response_trend, severity_breakdown, analyst_workload, rule_effectiveness, source_reliability, incident_classifications } = analytics;

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Security Analytics</h1>
        <span className="text-xs text-gray-400">90-day window</span>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <MetricCard label="Alerts (90d)" value={summary.total_alerts_90d} />
        <MetricCard label="Incidents (90d)" value={summary.total_incidents_90d} />
        <MetricCard label="Open Alerts" value={summary.open_alerts} />
        <MetricCard label="Open Incidents" value={summary.open_incidents} />
        <MetricCard label="False Positive %" value={`${summary.false_positive_rate}%`} />
        <MetricCard label="Avg MTTD" value={summary.avg_mttd_minutes != null ? `${summary.avg_mttd_minutes}m` : '--'} sub="Mean Time to Detect" />
        <MetricCard label="Avg MTTR" value={summary.avg_mttr_minutes != null ? `${summary.avg_mttr_minutes}m` : '--'} sub="Mean Time to Respond" />
      </div>

      {/* ── Community Benchmarking ── */}
      {benchmarks && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-5 border border-indigo-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-indigo-900">Community Benchmarking</h2>
              <p className="text-[10px] text-indigo-500 mt-0.5">
                {benchmarks.collective_configured
                  ? `Compared against ${benchmarks.sample_size > 0 ? `${benchmarks.sample_size} MSPs` : 'community averages'} via Collective`
                  : 'Compared against industry averages (connect to Collective for live data)'}
              </p>
            </div>
            {!benchmarks.collective_configured && (
              <span className="text-[10px] px-2 py-1 bg-indigo-100 text-indigo-600 rounded-full font-medium">Industry defaults</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <BenchmarkCard label="Mean Time to Detect" comparison={benchmarks.comparisons.mttd} />
            <BenchmarkCard label="Mean Time to Respond" comparison={benchmarks.comparisons.mttr} />
            <BenchmarkCard label="False Positive Rate" comparison={benchmarks.comparisons.false_positive_rate} unit="%" />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="bg-white/70 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 uppercase">Alerts/Week</p>
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-bold text-gray-900">{benchmarks.comparisons.alert_volume.yours}</span>
                <span className="text-xs text-gray-400">vs {benchmarks.comparisons.alert_volume.community_avg} avg</span>
              </div>
            </div>
            <div className="bg-white/70 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 uppercase">Incidents/Week</p>
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-bold text-gray-900">{benchmarks.comparisons.incident_volume.yours}</span>
                <span className="text-xs text-gray-400">vs {benchmarks.comparisons.incident_volume.community_avg} avg</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Row 1: Volume + Severity ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Alert Volume Trend</h2>
          <BarChart
            data={alert_volume_trend.map(w => ({
              label: w.week.slice(5), // MM-DD
              value: w.total,
              color: w.critical > 0 ? '#dc2626' : w.high > 0 ? '#ea580c' : '#6366f1',
            }))}
          />
          <div className="flex gap-4 mt-3 text-[10px] text-gray-400">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-600" /> Has critical</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-600" /> Has high</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-500" /> Normal</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Severity Breakdown</h2>
          <SeverityDonut breakdown={severity_breakdown} />
        </div>
      </div>

      {/* ── Row 2: Response Trend + Source Reliability ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Response Time Trend (MTTD / MTTR)</h2>
          {response_trend.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">No response data yet</p>
          ) : (
            <>
              <div className="flex items-end gap-1" style={{ height: 120 }}>
                {response_trend.map((w, i) => {
                  const mttd = w.avg_mttd ?? 0;
                  const mttr = w.avg_mttr ?? 0;
                  const max = Math.max(...response_trend.map(r => Math.max(r.avg_mttd ?? 0, r.avg_mttr ?? 0)), 1);
                  return (
                    <div key={i} className="flex-1 flex gap-px items-end h-full" title={`${w.week}: MTTD=${mttd}m, MTTR=${mttr}m`}>
                      <div className="flex-1 bg-blue-400 rounded-t" style={{ height: `${(mttd / max) * 100}%`, minHeight: mttd > 0 ? 2 : 0 }} />
                      <div className="flex-1 bg-amber-400 rounded-t" style={{ height: `${(mttr / max) * 100}%`, minHeight: mttr > 0 ? 2 : 0 }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[9px] text-gray-400">{response_trend[0]?.week.slice(5)}</span>
                <span className="text-[9px] text-gray-400">{response_trend[response_trend.length - 1]?.week.slice(5)}</span>
              </div>
              <div className="flex gap-4 mt-2 text-[10px] text-gray-400">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" /> MTTD</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> MTTR</span>
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Source Reliability</h2>
          {source_reliability.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">No source data</p>
          ) : (
            <div className="space-y-3">
              {source_reliability.slice(0, 8).map(src => (
                <div key={src.source} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-24 truncate" title={src.source}>{src.source}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${src.reliability_score}%`,
                        backgroundColor: src.reliability_score >= 80 ? '#16a34a' : src.reliability_score >= 50 ? '#d97706' : '#dc2626',
                      }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700 w-10 text-right">{src.reliability_score}%</span>
                  <span className="text-[10px] text-gray-400 w-16 text-right">{src.total_alerts} alerts</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Row 3: Analyst Workload + Rule Effectiveness ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Analyst Workload</h2>
          {analyst_workload.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">No assignment data</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-400 border-b">
                    <th className="pb-2 font-medium">Analyst</th>
                    <th className="pb-2 font-medium text-right">Open Alerts</th>
                    <th className="pb-2 font-medium text-right">Open Incidents</th>
                    <th className="pb-2 font-medium text-right">Resolved (90d)</th>
                  </tr>
                </thead>
                <tbody>
                  {analyst_workload.slice(0, 10).map(w => (
                    <tr key={w.analyst} className="border-b border-gray-50">
                      <td className="py-2 text-gray-700 font-medium truncate max-w-[150px]">{w.analyst}</td>
                      <td className="py-2 text-right">
                        <span className={w.open_alerts > 10 ? 'text-red-600 font-semibold' : 'text-gray-600'}>{w.open_alerts}</span>
                      </td>
                      <td className="py-2 text-right">
                        <span className={w.open_incidents > 3 ? 'text-red-600 font-semibold' : 'text-gray-600'}>{w.open_incidents}</span>
                      </td>
                      <td className="py-2 text-right text-gray-600">{w.resolved_90d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Detection Rule Effectiveness</h2>
          {rule_effectiveness.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">No detection rule data</p>
          ) : (
            <div className="space-y-3">
              {rule_effectiveness.slice(0, 8).map(r => (
                <div key={r.rule_id} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-20 truncate font-mono" title={r.rule_id}>{r.rule_id}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${r.precision}%`,
                        backgroundColor: r.precision >= 90 ? '#16a34a' : r.precision >= 70 ? '#d97706' : '#dc2626',
                      }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700 w-10 text-right">{r.precision}%</span>
                  <span className="text-[10px] text-gray-400 w-16 text-right">{r.hits} hits</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Row 4: Incident Classifications ── */}
      {Object.keys(incident_classifications).length > 0 && (
        <div className="bg-white rounded-lg shadow p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Incident Classifications</h2>
          <BarChart
            data={Object.entries(incident_classifications)
              .sort(([, a], [, b]) => b - a)
              .map(([cls, count]) => ({
                label: cls.replace(/_/g, ' '),
                value: count,
                color: '#6366f1',
              }))}
            maxH={80}
          />
        </div>
      )}
    </div>
  );
}
